import React, {createRef, memo, useContext, useEffect, useRef, useState} from "react";
import useWebRtcClient from "../../webrtc/WebRtcClient";
import {ConferenceUser, ConferenceUserWithStatus} from "@inpriton/types";
import MediaContext from "../../context/MediaContext";
import AppContext from "../../context/AppContext";

import './VideoGrid.scss'
import {useTranslation} from "react-i18next";
import MaterialIcon from "../icons/MaterialIcon";
import UserStatusSymbols from "../general/UserStatusSymbols";
import ErrorBoundary from "../general/ErrorBoundary";
import Button from "../general/Button";

const TALKING_SAMPLE_MS = 100
const TALKING_THRESHOLD = 1

export const RemoteVideo = memo((props: { user: ConferenceUserWithStatus }) => {
  const {t} = useTranslation()

  const {
    userData,
    users,
    streams,
    userVolumes,
    conferenceStatus,
  } = useContext(AppContext)

  // RISKS.md/P2.34: do NOT collapse this back to `props.user` — `memo(…, () => true)` pins it at
  // mount. It survives only because the `user:status` handler mutates the same roster entry in
  // place; a handler that returns fresh objects (`admitted` does) orphans it.
  const liveUser = users.find(u => u.uuid === props.user.uuid) ?? props.user

  const {
    videoStream,
    cameraUnavailable,
    cameraStartingVisible,
    videoEnabled,
    selectedSpeaker,
    applySinkId,
  } = useContext(MediaContext)

  const [talking, setTalking] = useState(false)
  const talkingRef = useRef(false)

  const uuid = props.user.uuid

  const ref = createRef<HTMLVideoElement>()

  // `?? 1`: at admission a peer's tile mounts before useRoster stores its volume, and
  // `volume = undefined` throws (RISKS.md/P2.47).
  const userVolume = userVolumes[uuid] ?? 1

  const [userStream, setUserStream] = useState<MediaStream|null>(null)

  const isOwnStream = props.user.uuid === userData?.uuid

  const isAttendee = userData && !userData.isTranslator && !userData.isModerator
  const assignedTranslatorUuids = conferenceStatus.userPreferences?.assignedTranslatorUuids ?? []
  const speakersToFilter = conferenceStatus.userPreferences?.speakersToFilter ?? []
  const simultanConfig = conferenceStatus.userPreferences?.simultanConfig ?? null

  const isFilteredSpeaker = speakersToFilter.includes(uuid)

  const isAutoMuted = !isOwnStream && (
    // Non-assigned translator: muted for everyone
    (props.user.isTranslator && (
      (!!isAttendee && !assignedTranslatorUuids.includes(uuid))
      || !!userData?.isTranslator
    ))
    // Speaker in speakersToFilter with simultanConfig=2: fully muted
    || (isFilteredSpeaker && simultanConfig === 2)
  )

  const isVolumeReduced = !isOwnStream && isFilteredSpeaker && simultanConfig === 1

  const effectiveMuted = isOwnStream || isAutoMuted

  useEffect(() => {
    const newStream = isOwnStream
      ? videoStream
      : (
        props.user && streams[uuid] && streams[uuid]['camera']
          ? streams[uuid]['camera'].stream
          : null
      )

    if (userStream && userStream?.id === newStream?.id) {
      return
    }

    console.log('stream changed', props.user.uuid, userStream?.id, newStream?.id)
    setUserStream(newStream)
  }, [streams])

  // RISKS.md/P1.25 + P2.19: local state, not context — a context write per tick re-renders every
  // consumer at 10 Hz per audio peer.
  const meter = isOwnStream ? null : (streams[uuid]?.['camera']?.volumeMeter ?? null)
  useEffect(() => {
    if (!meter) {
      talkingRef.current = false
      setTalking(false)
      return
    }
    const sample = () => {
      const next = meter.getVolume() > TALKING_THRESHOLD
      if (next === talkingRef.current) return
      talkingRef.current = next
      setTalking(next)
    }
    const interval = setInterval(sample, TALKING_SAMPLE_MS)
    return () => clearInterval(interval)
  }, [meter])

  useEffect(() => {
    if (ref.current) {
      ref.current.srcObject = userStream
    }
  }, [userStream]);

  // `ref` stays out of the deps: a createRef, rebuilt every render (RISKS.md/P2.40). The own tile is
  // muted, so routing it would only move audio nobody hears (RISKS.md/P2.39).
  useEffect(() => {
    const el = ref.current
    if (!el || isOwnStream) return
    applySinkId(el)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applySinkId, selectedSpeaker, userStream])

  useEffect(() => {
    if (ref.current) {
      ref.current.muted = effectiveMuted
      ref.current.volume = effectiveMuted ? 0 : (isVolumeReduced ? 0.2 : userVolume)
    }
  }, [ref, userStream, userVolume, effectiveMuted, isVolumeReduced])

  // The roster for everyone, ourselves included — per session, never per browser (RISKS.md/P2.48).
  const name = isOwnStream
    ? (liveUser.name || userData?.name || t('you'))
    : liveUser.name

  // Does this tile carry a picture? For ourselves intent AND device usability; for everyone else
  // the roster is the only truth. UserStatusSymbols must use this EXACT predicate for its chip, or
  // the avatar and the chip disagree on the same tile (RISKS.md/P2.32, P2.34).
  const hasVideo = isOwnStream
    ? (videoEnabled && !cameraUnavailable)
    : !!liveUser.status.streams['camera']?.['video']

  // RISKS.md/P2.33: the element's own frame state, because the roster cannot be trusted here —
  // peers publish a PRODUCER ID that only the explicit camera-off toggle clears, so a BLOCKED
  // camera leaves a stale id claiming a picture that never arrives.
  const [hasFrames, setHasFrames] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // `el` in a local: `ref` is a createRef() rebuilt every render, so the object captured here may
    // already be nulled by the time the cleanup runs. That also means `ref` in the deps below changes
    // identity on EVERY render, so this effect re-attaches its listeners constantly — harmless only
    // because `bound` starts null each run, which makes `hadTrack` false and stops the srcObject
    // re-attach from thrashing. `useRef` would fix the cause (RISKS.md/P2.40).
    //
    // RISKS.md/P2.38: read the track LIVE, never capture it in this closure. The own tile's
    // `userStream` is a MediaStream mutated in place, so its identity never changes and this effect
    // does NOT re-run when the camera returns with a new track — a captured reference would go on
    // answering for the dead one and `hasFrames` could never recover. Remote tiles are exempt: a VIDEO
    // change replaces the stream object; an audio-only one mutates in place (RISKS.md/P1.22).
    let bound: MediaStreamTrack | null = null
    const clear = () => setHasFrames(false)
    const check = () => {
      const track = userStream?.getVideoTracks()[0] ?? null
      bind(track)
      setHasFrames(
        el.videoWidth > 0 && el.videoHeight > 0
        && !!track && track.readyState === 'live' && !track.muted,
      )
    }
    // Re-bind the track-level listeners whenever the live track changes, for the same reason.
    const bind = (track: MediaStreamTrack | null) => {
      if (track === bound) return
      bound?.removeEventListener('mute', clear)
      bound?.removeEventListener('unmute', check)
      const hadTrack = bound !== null
      bound = track
      bound?.addEventListener('mute', clear)
      bound?.addEventListener('unmute', check)
      // RISKS.md/P2.38: re-attach on a REPLACED track. `srcObject` is assigned in an effect keyed on
      // [userStream], so for the own tile it is set once at mount and the element is never told its
      // tracks were swapped — it then reports videoWidth 0 and fires nothing. Nulling first forces
      // the reload, whose `loadedmetadata` re-runs check().
      if (hadTrack && track && el.srcObject !== null) {
        el.srcObject = null
        el.srcObject = userStream ?? null
      }
    }
    el.addEventListener('loadedmetadata', check)
    el.addEventListener('resize', check)
    el.addEventListener('playing', check)
    el.addEventListener('emptied', clear)
    check()
    return () => {
      el.removeEventListener('loadedmetadata', check)
      el.removeEventListener('resize', check)
      el.removeEventListener('playing', check)
      el.removeEventListener('emptied', clear)
      bind(null)
    }
    // `hasVideo` is in the deps as a guaranteed re-run when the OWN camera returns — the element
    // events are the normal trigger, but nothing observes a MediaStream mutation.
  }, [ref, userStream, hasVideo])

  // Asymmetric on purpose: frames hide the placeholder INSTANTLY, their absence only shows it after
  // a grace period — a remote tile needs transport + DTLS + consume + a keyframe before it can paint.
  const [noPictureGraceElapsed, setNoPictureGraceElapsed] = useState(false)
  useEffect(() => {
    if (hasFrames) {
      setNoPictureGraceElapsed(false)
      return
    }
    const timer = setTimeout(() => setNoPictureGraceElapsed(true), 2000)
    return () => clearTimeout(timer)
  }, [hasFrames, userStream])

  // RISKS.md/P2.38: one shared signal, delayed in MediaContext so both rooms behave identically.
  // It deliberately does NOT fire on an OS re-enable — capture starts within a millisecond of Chrome
  // reporting the device, so there is nothing to wait for. It earns its place on a cold start, where
  // MediaPipe's init takes ~4s.
  const showStarting = isOwnStream && videoEnabled && cameraStartingVisible
  // Starting up is not "off". Mutually exclusive with the starting label by construction: once that
  // is up the avatar stands down, so a re-capture can never show both, nor flicker between them.
  const showInitial = isOwnStream
    ? (!videoEnabled || (cameraUnavailable && !showStarting))
    : (!hasFrames && (!hasVideo || noPictureGraceElapsed))

  // RISKS.md/P2.33: NOT just `showInitial` — revealing the element the moment intent flips shows
  // its previous frame at the wrong size, which then visibly resizes as real frames arrive.
  const hideVideo = showInitial || showStarting || !hasFrames

  const initial = (name || '').trim().charAt(0).toUpperCase()

  const classNames = ['video-frame']
  if (talking) {
    classNames.push('talking')
  }

  return (
    <>
      <video
        key={`remote-video-${uuid}`}
        ref={ref}
        autoPlay
        muted={isOwnStream}
        // Hidden rather than unmounted: unmounting drops the ref and forces a re-attach on every
        // toggle (RISKS.md/P2.32).
        className={hideVideo ? 'no-picture' : undefined}
        // RISKS.md/P2.32: do NOT add a `poster` to the own tile — a browser paints it ONLY before
        // playback begins, so it flashes on entry and can never come back once frames have rendered.
        style={{width: '100%'}}
      >
      </video>
      {/* RISKS.md/P2.32: the meeting room draws NO distinction between "camera off" and "camera
          blocked" — one initial for both; the green room is where that difference is explained.
          Name and chips must follow this in the DOM so they stay readable on top. */}
      {showInitial && (
        <>
          <span className="tile-backdrop" aria-hidden="true"/>
          <span className="initial-avatar" aria-hidden="true">{initial}</span>
        </>
      )}
      {showStarting && (
        <>
          <span className="tile-backdrop" aria-hidden="true"/>
          <span className="tile-status">{t('lobby.cameraStarting')}</span>
        </>
      )}
      <div className={classNames.join(' ')}>
        {/* RISKS.md/P2.33: name pill and device chips are SIBLINGS, not chips nested in the pill. */}
        <div className="name-row">
          <div className="name">{name}</div>
          <UserStatusSymbols user={liveUser}/>
        </div>
      </div>
    </>
  )
}, () => true)

// A Try again that fails at once mounts a new fallback, which takes the focus back. The mark lasts
// one task; a click's update renders and flushes its effects before the next one.
const retriedTiles = new Set<string>()

// RISKS.md/P2.77: nothing of RemoteVideo's, UserStatusSymbols included: whatever made the tile
// fail would throw here again, and a fallback's throw reaches the routes' boundary, which ends the
// session.
const TileFailed = (props: { uuid: string, name?: string, isOwn: boolean, onRetry: () => void }) => {
  const {t} = useTranslation()

  const retry = () => {
    retriedTiles.add(props.uuid)
    setTimeout(() => retriedTiles.delete(props.uuid))
    props.onRetry()
  }

  return (
    <>
      <span className="tile-backdrop" aria-hidden="true"/>
      <div className="tile-failed">
        <span>{t(props.isOwn ? 'videoGrid.ownTileFailed' : 'videoGrid.tileFailed')}</span>
        <Button
          onClick={retry}
          size={'md'}
          primary
          ariaLabel={props.name ? `${t('lobby.retry')}: ${props.name}` : undefined}
          autoFocus={retriedTiles.has(props.uuid)}
        >
          {t('lobby.retry')}
        </Button>
      </div>
      <div className="video-frame">
        <div className="name-row">
          <div className="name">{props.name}</div>
        </div>
      </div>
    </>
  )
}

export default memo((props: { users: ConferenceUserWithStatus[] }) => {
  const {
    userData,
  } = useContext(AppContext)

  const users = props.users.sort((u1, u2) => {
    if (u1.uuid === userData?.uuid) {
      return -1
    }
    if (u2.uuid === userData?.uuid) {
      return 1
    }
    return 0
  })

  return (
    <ul className={`video-grid cnt-${users.filter(u => u.online).length}`}>
      {users.filter(user => user.online).map(user => (
        <li key={`video-${user.uuid}`} data-name={user.name} data-uid={user.uuid}>
          <ErrorBoundary fallback={retry => (
            <TileFailed uuid={user.uuid} name={user.name} isOwn={user.uuid === userData?.uuid} onRetry={retry}/>
          )}>
            <RemoteVideo user={user}/>
          </ErrorBoundary>
        </li>
      ))}
    </ul>
  )
}, (now: { users: ConferenceUser[] }, before: { users: ConferenceUser[] }) => {
  const isEqual = now.users.filter(u => u.online).map(u => u.uuid).sort().join(',') === before.users.filter(u => u.online).map(u => u.uuid).sort().join(',')
  return isEqual
})